import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { IconButton, Button } from '@mui/material';

const ACCENT_BLUE = '#22638f';
const ACCENT_ORANGE = '#d96c3c';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 460,
  bgcolor: '#fff',
  border: `1px solid ${BORDER}`,
  boxShadow: '0 12px 40px rgba(12, 61, 110, 0.18)',
  borderRadius: '16px',
  p: 3.5,
};

// pick color/icon based on title
function getVariant(title) {
  const t = (title || '').toLowerCase();
  if (t === 'warning') return { color: ACCENT_ORANGE, bg: 'rgba(217, 108, 60, 0.1)', Icon: WarningAmberIcon };
  if (t === 'success' || t === 'sucess') return { color: '#2e7d32', bg: 'rgba(46, 125, 50, 0.1)', Icon: CheckCircleIcon };
  if (t === 'alert') return { color: '#d32f2f', bg: 'rgba(211, 47, 47, 0.1)', Icon: ErrorOutlineIcon };
  return { color: ACCENT_BLUE, bg: 'rgba(34, 99, 143, 0.1)', Icon: ErrorOutlineIcon };
}

function PopUpMessage({ open, setOpen, title, message, isLoading }) {
  const messageLines = message?.split('\n');
  const handleClose = () => setOpen(!open);
  const { color, bg, Icon } = getVariant(title);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEnforceFocus
      sx={{ '& .MuiBackdrop-root': { pointerEvents: 'none' } }}
    >
      <Box sx={style}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              sx={{
                width: 34, height: 34, borderRadius: '10px',
                backgroundColor: bg, color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon sx={{ fontSize: 19 }} />
            </Box>
            <Typography id="modal-modal-title" sx={{ fontSize: 16, fontWeight: 700, color: TEXT_BODY }}>
              {title}
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={handleClose} sx={{ color: TEXT_MUTED }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box id="modal-modal-description" sx={{ mt: 2 }}>
          {isLoading ? (
            <Backdrop open={true} sx={{ position: 'absolute', inset: 0, borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <CircularProgress sx={{ color: ACCENT_BLUE }} />
            </Backdrop>
          ) : (
            <>
              <Box sx={{ fontSize: 13.5, color: TEXT_BODY, lineHeight: 1.7 }}>
                {messageLines?.length > 0 &&
                  messageLines.map((line, index) => {
                    const match = line.match(/^\*\*(Sub-Scan \d+)\*\*\s*:\s*(.*)$/);
                    return (
                      <React.Fragment key={index}>
                        {match ? (
                          <>
                            <strong style={{ color }}>{match[1]}</strong>: {match[2]}
                          </>
                        ) : (
                          line
                        )}
                        <br />
                      </React.Fragment>
                    );
                  })}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  onClick={handleClose}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: 'none',
                    backgroundColor: ACCENT_BLUE,
                    color: '#fff',
                    '&:hover': { backgroundColor: '#1b4f73', boxShadow: 'none' },
                  }}
                >
                  OK
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default PopUpMessage;